/**
 * Menu enrichment data — descriptions, dietary tags, station, image hints
 * keyed by a normalized dish name so we can match against server rows that
 * may have slight spelling variations (Poori vs Puri, Chat vs Chaat, etc.).
 *
 * Backend menu (server-side) supplies name/section/station/restrictions.
 * This layer overlays descriptions, dietary chips, and visual identity
 * (emoji + gradient) without touching backend business logic.
 */

// Normalize: lowercase, strip diacritics, keep only a-z; collapses "Bhel Poori" and "Bhel Puri" to "bhelpuri"
export function nkey(s) {
  return (s || "")
    .toString()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

// Category taxonomy (top-level nav in the explorer)
export const CATEGORIES = [
  { id: "cocktail_hour", label: "Cocktail Hour", emoji: "🥂" },
  { id: "dinner", label: "Dinner", emoji: "🍽️" },
  { id: "dessert", label: "Desserts", emoji: "🍰" },
  { id: "after_party", label: "After Party", emoji: "🌙" },
];

// Backend section → top-level category
export function sectionToCategory(section) {
  if (section === "cocktail_hour") return "cocktail_hour";
  if (section === "dessert") return "dessert";
  if (section === "after_party") return "after_party";
  if (["first_course", "second_course", "family_style"].includes(section)) return "dinner";
  return "cocktail_hour";
}

// Sub-station label — clean up backend station strings like "At Table - 6:15 PM"
export function displayStation(item) {
  const s = item.section;
  const st = (item.station || "").trim();
  if (s === "first_course") return "First Course";
  if (s === "second_course") return "Palate Cleanser";
  if (s === "family_style") return "Main Course";
  if (s === "dessert") {
    if (st.toLowerCase().includes("chai")) return "Chai Station";
    return "Desserts";
  }
  if (s === "after_party") return "Late Night";
  if (st.toLowerCase() === "passed") return "Passed Hors d'oeuvres";
  return st || "";
}

// Couple's fun station names (must match the venue map labels in venueMapConfig.js)
const STATION_RENAMES = {
  "Bombay Station": "Poori Party",
  "Chaat Station": "Let's Chaat About It",
  "Tawa Station": "Tikka Chance On Me",
  "Chinese Station": "Wok'n Roll",
  "Malabar Station": "Satis-frying",
};

// Ordered station chip lists per category
export const STATION_ORDER = {
  cocktail_hour: [
    "Poori Party",
    "Let's Chaat About It",
    "Tikka Chance On Me",
    "Wok'n Roll",
    "Satis-frying",
    "Passed Hors d'oeuvres",
  ],
  dinner: ["First Course", "Palate Cleanser", "Main Course"],
  dessert: ["Desserts", "Chai Station"],
  after_party: ["Late Night"],
};

// Normalize station display to match STATION_ORDER (e.g. "Chat Station" -> "Chaat Station"),
// then apply the couple's fun station names.
export function canonStation(label) {
  if (!label) return label;
  let l = label;
  if (nkey(l) === "chatstation") l = "Chaat Station";
  return STATION_RENAMES[l] || l;
}

// Dietary tag definitions with color + glyph
export const TAG_DEFS = {
  vegetarian: { label: "V", tone: "#10b981", full: "Vegetarian" },
  vegan: { label: "VG", tone: "#059669", full: "Vegan" },
  "gluten-free": { label: "GF", tone: "#8b5cf6", full: "Gluten-free" },
  seafood: { label: "Seafood", tone: "#0891b2", full: "Seafood" },
  chicken: { label: "Chicken", tone: "#d97706", full: "Chicken" },
  lamb: { label: "Lamb", tone: "#b91c1c", full: "Lamb" },
  beef: { label: "Beef", tone: "#7f1d1d", full: "Beef" },
  spicy: { label: "Spicy", tone: "#ef4444", full: "Spicy" },
  dessert: { label: "Dessert", tone: "#ec4899", full: "Dessert" },
  latenight: { label: "Late Night", tone: "#6366f1", full: "Late Night" },
};

// Filter chip choices for the explorer
export const FILTER_CHIPS = [
  { id: "vegetarian", label: "Vegetarian" },
  { id: "vegan", label: "Vegan" },
  { id: "gluten-free", label: "Gluten Free" },
  { id: "seafood", label: "Seafood" },
  { id: "chicken", label: "Chicken" },
  { id: "lamb", label: "Lamb" },
  { id: "beef", label: "Beef" },
  { id: "dessert", label: "Dessert" },
  { id: "latenight", label: "Late Night" },
];

/**
 * Per-dish enrichment. Keys are normalized names (nkey()).
 * emoji + gradient produce a consistent square tile that always renders.
 * image points at the extracted Excel photo saved under /menu-photos/.
 * The card gracefully falls back to the emoji tile if the image fails.
 */

// Base path for wedding menu photos extracted from the couple's workbook.
const PHOTO_BASE = "/menu-photos";
import { MENU_IMAGES } from "./menuImages";

export const DISH_DATA = {
  // --- Bombay Station ---
  bhelpuri: {
    description: "A crunchy, tangy street-food favorite tossed with puffed rice, chutneys, and fresh herbs.",
    tags: ["vegetarian", "spicy"], emoji: "🍥", grad: ["#fed7aa", "#fdba74"],
    image: `${PHOTO_BASE}/bhel-puri.jpg`,
  },
  bhelpoori: {
    description: "A crunchy, tangy street-food favorite tossed with puffed rice, chutneys, and fresh herbs.",
    tags: ["vegetarian", "spicy"], emoji: "🍥", grad: ["#fed7aa", "#fdba74"],
    image: `${PHOTO_BASE}/bhel-puri.jpg`,
  },
  panipuri: {
    description: "Crispy shells filled with spiced potatoes and refreshing mint-tamarind water—one bite, big flavor.",
    tags: ["vegetarian", "spicy"], emoji: "🫧", grad: ["#bbf7d0", "#86efac"],
    image: `${PHOTO_BASE}/pani-puri.jpg`,
  },
  paanipoori: {
    description: "Crispy shells filled with spiced potatoes and refreshing mint-tamarind water—one bite, big flavor.",
    tags: ["vegetarian", "spicy"], emoji: "🫧", grad: ["#bbf7d0", "#86efac"],
    image: `${PHOTO_BASE}/pani-puri.jpg`,
  },
  // --- Chaat Station ---
  alootikkichaat: {
    description: "Golden potato patties topped with yogurt, chutneys, and a burst of sweet, spicy, and tangy flavors.",
    tags: ["vegetarian", "spicy"], emoji: "🥔", grad: ["#fde68a", "#fcd34d"],
    image: `${PHOTO_BASE}/aloo-tikki-chaat.jpg`,
  },
  alootikkichaat: {
    description: "Golden potato patties topped with yogurt, chutneys, and a burst of sweet, spicy, and tangy flavors.",
    tags: ["vegetarian", "spicy"], emoji: "🥔", grad: ["#fde68a", "#fcd34d"],
    image: `${PHOTO_BASE}/aloo-tikki-chaat.jpg`,
  },
  samosachaat: {
    description: "Flaky samosas transformed into the ultimate comfort chaat with chickpeas, yogurt, and chutneys.",
    tags: ["vegetarian", "spicy"], emoji: "🥟", grad: ["#fed7aa", "#fb923c"],
    image: `${PHOTO_BASE}/samosa-chaat.jpg`,
  },
  samosachaat: {
    description: "Flaky samosas transformed into the ultimate comfort chaat with chickpeas, yogurt, and chutneys.",
    tags: ["vegetarian", "spicy"], emoji: "🥟", grad: ["#fed7aa", "#fb923c"],
    image: `${PHOTO_BASE}/samosa-chaat.jpg`,
  },
  // --- Tawa Station ---
  chickentikka: {
    description: "Tender chicken marinated in aromatic spices and flame-grilled to perfection.",
    tags: ["chicken", "gluten-free", "spicy"], emoji: "🍗", grad: ["#fecaca", "#fca5a5"],
    image: `${PHOTO_BASE}/chicken-tikka.jpg`,
  },
  haryalitikka: {
    description: "Juicy chicken infused with fresh cilantro, mint, and herbs for a vibrant, smoky bite.",
    tags: ["chicken", "gluten-free"], emoji: "🌿", grad: ["#bbf7d0", "#4ade80"],
    image: `${PHOTO_BASE}/haryali-tikka.jpg`,
  },
  chilipaneerontawa: {
    description: "Crispy paneer tossed with peppers, onions, and bold Indo-Chinese flavors.",
    tags: ["vegetarian", "spicy"], emoji: "🌶️", grad: ["#fecaca", "#f87171"],
    image: `${PHOTO_BASE}/chili-paneer-on-tawa.jpg`,
  },
  // --- Chinese Station ---
  hakkanoodles: {
    description: "Wok-tossed noodles with crisp vegetables and savory Asian-inspired seasonings.",
    tags: ["vegetarian"], emoji: "🍜", grad: ["#fef3c7", "#fbbf24"],
    image: `${PHOTO_BASE}/hakka-noodles.jpg`,
  },
  vegetablemanchurian: {
    description: "Crispy vegetable dumplings coated in a rich, tangy garlic-soy sauce.",
    tags: ["vegetarian"], emoji: "🥬", grad: ["#dcfce7", "#86efac"],
    image: `${PHOTO_BASE}/vegetable-manchurian.jpg`,
  },
  vegmanchurian: {
    description: "Crispy vegetable dumplings coated in a rich, tangy garlic-soy sauce.",
    tags: ["vegetarian"], emoji: "🥬", grad: ["#dcfce7", "#86efac"],
    image: `${PHOTO_BASE}/vegetable-manchurian.jpg`,
  },
  kungpaochilichicken: {
    description: "A spicy stir-fry of chicken, peppers, and bold flavors with just the right kick.",
    tags: ["chicken", "spicy"], emoji: "🌶️", grad: ["#fecaca", "#ef4444"],
    image: `${PHOTO_BASE}/kung-pao-chili-chicken.jpg`,
  },
  kungpaochillichicken: {
    description: "A spicy stir-fry of chicken, peppers, and bold flavors with just the right kick.",
    tags: ["chicken", "spicy"], emoji: "🌶️", grad: ["#fecaca", "#ef4444"],
    image: `${PHOTO_BASE}/kung-pao-chili-chicken.jpg`,
  },
  // --- Malabar Station ---
  shrimpsaute: {
    description: "Succulent shrimp cooked with fragrant spices and coastal-inspired flavors.",
    tags: ["seafood", "gluten-free"], emoji: "🦐", grad: ["#fbcfe8", "#f9a8d4"],
    image: `${PHOTO_BASE}/shrimp-saute.jpg`,
  },
  chickensaute: {
    description: "Tender chicken sautéed with herbs and spices for a simple, flavorful bite.",
    tags: ["chicken", "gluten-free"], emoji: "🍗", grad: ["#fed7aa", "#fdba74"],
    image: `${PHOTO_BASE}/chicken-saute.jpg`,
  },
  // --- Passed Hors d'oeuvres ---
  lambchop: {
    description: "Perfectly seasoned lamb chops, grilled until juicy and tender.",
    tags: ["lamb", "gluten-free"], emoji: "🍖", grad: ["#fecaca", "#b91c1c"],
    image: `${PHOTO_BASE}/lamb-chop.jpg`,
  },
  fishkoliwada: {
    description: "Mumbai's famous crispy fried fish, lightly spiced and irresistibly crunchy.",
    tags: ["seafood", "spicy"], emoji: "🐟", grad: ["#bfdbfe", "#60a5fa"],
    image: `${PHOTO_BASE}/fish-koliwada.jpg`,
  },
  cutmirchipoppers: {
    description: "Mild peppers stuffed, battered, and fried for the perfect savory snack.",
    tags: ["vegetarian", "spicy"], emoji: "🌶️", grad: ["#dcfce7", "#4ade80"],
    image: `${PHOTO_BASE}/cut-mirchi-poppers.jpg`,
  },
  masalagnocchi: {
    description: "Pillowy Italian gnocchi meets bold Indian spices in a delicious fusion dish.",
    tags: ["vegetarian"], emoji: "🥟", grad: ["#fef3c7", "#fde68a"],
    image: `${PHOTO_BASE}/masala-gnocchi.jpg`,
  },
  vegburratalollipop: {
    description: "Creamy burrata with fresh vegetables in a fun, bite-sized appetizer.",
    tags: ["vegetarian"], emoji: "🥗", grad: ["#e0e7ff", "#a5b4fc"],
    image: `${PHOTO_BASE}/veg-burrata-lollipop.jpg`,
  },
  // --- First Course ---
  chefsalad: {
    description: "A fresh mix of seasonal greens and vegetables with a light, refreshing finish.",
    tags: ["vegetarian", "gluten-free"], emoji: "🥗", grad: ["#dcfce7", "#86efac"],
    image: `${PHOTO_BASE}/chefs-salad.jpg`,
  },
  patranifish: {
    description: "Fish wrapped in a vibrant herb chutney and gently steamed for delicate flavor.",
    tags: ["seafood", "gluten-free"], emoji: "🐟", grad: ["#bbf7d0", "#86efac"],
    image: `${PHOTO_BASE}/patrani-fish.jpg`,
  },
  partranifish: {
    description: "Fish wrapped in a vibrant herb chutney and gently steamed for delicate flavor.",
    tags: ["seafood", "gluten-free"], emoji: "🐟", grad: ["#bbf7d0", "#86efac"],
    image: `${PHOTO_BASE}/patrani-fish.jpg`,
  },
  // --- Palate Cleanser ---
  fruitpalatecleanser: {
    description: "A refreshing interlude to awaken your palate before the main course.",
    tags: ["vegan", "vegetarian", "gluten-free"], emoji: "🍧", grad: ["#fce7f3", "#f9a8d4"],
    image: `${PHOTO_BASE}/fruit-palate-cleanser.jpg`,
  },
  fruitpatatecleanser: {
    description: "A refreshing interlude to awaken your palate before the main course.",
    tags: ["vegan", "vegetarian", "gluten-free"], emoji: "🍧", grad: ["#fce7f3", "#f9a8d4"],
    image: `${PHOTO_BASE}/fruit-palate-cleanser.jpg`,
  },
  // --- Main Course ---
  freshseekhkabab: {
    description: "Juicy, spiced kebabs grilled over open flames for classic smoky flavor.",
    tags: ["beef", "gluten-free", "spicy"], emoji: "🍢", grad: ["#fecaca", "#f87171"],
    image: `${PHOTO_BASE}/fresh-seekh-kabab.jpg`,
  },
  butterchicken: {
    description: "Tender chicken in a rich, creamy tomato sauce that's always a crowd favorite.",
    tags: ["chicken", "gluten-free"], emoji: "🍛", grad: ["#fed7aa", "#f97316"],
    image: `${PHOTO_BASE}/butter-chicken.jpg`,
  },
  lambroganjosh: {
    description: "Slow-cooked lamb in a fragrant Kashmiri curry full of warming spices.",
    tags: ["beef", "gluten-free", "spicy"], emoji: "🍲", grad: ["#fca5a5", "#b91c1c"],
    image: `${PHOTO_BASE}/lamb-rogan-josh.jpg`,
  },
  palakpaneer: {
    description: "Soft paneer simmered in a creamy spinach sauce with classic North Indian flavors.",
    tags: ["vegetarian", "gluten-free"], emoji: "🥬", grad: ["#bbf7d0", "#16a34a"],
    image: `${PHOTO_BASE}/palak-paneer.jpg`,
  },
  dalmakhani: {
    description: "Slow-cooked black lentils finished with butter and cream for comforting richness.",
    tags: ["vegetarian", "gluten-free"], emoji: "🫘", grad: ["#e7d5b7", "#b8956a"],
    image: `${PHOTO_BASE}/dal-makhani.jpg`,
  },
  dalmakhni: {
    description: "Slow-cooked black lentils finished with butter and cream for comforting richness.",
    tags: ["vegetarian", "gluten-free"], emoji: "🫘", grad: ["#e7d5b7", "#b8956a"],
    image: `${PHOTO_BASE}/dal-makhani.jpg`,
  },
  vegetablebiryani: {
    description: "Fragrant basmati rice layered with vegetables, herbs, and aromatic spices.",
    tags: ["vegetarian", "gluten-free"], emoji: "🍚", grad: ["#fef3c7", "#fbbf24"],
    image: `${PHOTO_BASE}/vegetable-biryani.jpg`,
  },
  vegbiryani: {
    description: "Fragrant basmati rice layered with vegetables, herbs, and aromatic spices.",
    tags: ["vegetarian", "gluten-free"], emoji: "🍚", grad: ["#fef3c7", "#fbbf24"],
    image: `${PHOTO_BASE}/vegetable-biryani.jpg`,
  },
  freshassortedbreadbasket: {
    description: "A selection of warm Indian breads, perfect for scooping up every last bite.",
    tags: ["vegetarian"], emoji: "🫓", grad: ["#fed7aa", "#fdba74"],
    image: `${PHOTO_BASE}/fresh-assorted-bread-basket.jpg`,
  },
  // --- Desserts ---
  dryfruithalwa: {
    description: "A rich, nutty Indian sweet filled with dried fruits and warming flavors.",
    tags: ["vegetarian", "dessert"], emoji: "🍯", grad: ["#fef3c7", "#f59e0b"],
    image: `${PHOTO_BASE}/dry-fruit-halwa.jpg`,
  },
  doublekameetha: {
    description: "Hyderabad's beloved bread pudding, soaked in saffron milk and topped with nuts.",
    tags: ["vegetarian", "dessert"], emoji: "🍞", grad: ["#fed7aa", "#f59e0b"],
    image: `${PHOTO_BASE}/double-ka-meetha.jpg`,
  },
  doublekameeta: {
    description: "Hyderabad's beloved bread pudding, soaked in saffron milk and topped with nuts.",
    tags: ["vegetarian", "dessert"], emoji: "🍞", grad: ["#fed7aa", "#f59e0b"],
    image: `${PHOTO_BASE}/double-ka-meetha.jpg`,
  },
  freshjalebiwithrabdi: {
    description: "Hot, crispy jalebis paired with chilled, creamy rabdi—a timeless favorite.",
    tags: ["vegetarian", "dessert"], emoji: "🌀", grad: ["#fef3c7", "#fbbf24"],
    image: `${PHOTO_BASE}/fresh-jalebi-with-rabdi.jpg`,
  },
  cremebruleestation: {
    description: "Silky vanilla custard finished with a perfectly caramelized sugar crust.",
    tags: ["vegetarian", "dessert", "gluten-free"], emoji: "🍮", grad: ["#fef3c7", "#eab308"],
    image: `${PHOTO_BASE}/creme-brulee.jpg`,
  },
  chaistation: {
    description: "Freshly brewed masala chai—the perfect ending to a memorable meal.",
    tags: ["vegetarian", "gluten-free"], emoji: "☕", grad: ["#fed7aa", "#c2410c"],
    image: `${PHOTO_BASE}/chai-station.jpg`,
  },
  // --- After Party ---
  grilledcheesevadapav: {
    description: "Mumbai's iconic sandwich gets a cheesy, late-night twist.",
    tags: ["vegetarian", "latenight"], emoji: "🥪", grad: ["#fef3c7", "#fbbf24"],
    image: `${PHOTO_BASE}/grilled-cheese-vada-pav.jpg`,
  },
  gunpowdervadapao: {
    description: "Mumbai's iconic sandwich gets a cheesy, late-night twist.",
    tags: ["vegetarian", "latenight"], emoji: "🥪", grad: ["#fef3c7", "#fbbf24"],
    image: `${PHOTO_BASE}/grilled-cheese-vada-pav.jpg`,
  },
  milkshotswithcookies: {
    description: "A nostalgic sweet treat featuring cold milk and freshly baked cookies.",
    tags: ["vegetarian", "dessert", "latenight"], emoji: "🥛", grad: ["#e0e7ff", "#a5b4fc"],
    image: `${PHOTO_BASE}/milk-shots-with-cookies.jpg`,
  },
};

export function enrichDish(item) {
  const k = nkey(item.name);
  const data = DISH_DATA[k] || {};
  const imgs = MENU_IMAGES[k] || null;
  // Merge tags from backend restrictions + curated tags, dedupe
  const backendTags = (item.restrictions || []).map((r) => r.toLowerCase());
  const tagSet = new Set([...backendTags, ...(data.tags || [])]);
  return {
    ...item,
    description: data.description || item.description || "",
    tags: Array.from(tagSet),
    emoji: data.emoji || "🍽️",
    grad: data.grad || ["#f3e8d7", "#d4b280"],
    // v2 responsive derivatives (workbook masters) with legacy fallback
    image: imgs ? imgs.jpg : (data.image || null),
    thumb: imgs ? imgs.t : (data.image ? thumbPath(data.image) : null),
    card: imgs ? imgs.c : null,
    hero: imgs ? imgs.h : (data.image || null),
    imgMeta: imgs,
    stationLabel: canonStation(displayStation(item)),
    categoryId: sectionToCategory(item.section),
  };
}

// 320px card thumbnails live under /menu-photos/thumb/ (same filename).
// Detail modals keep the full-resolution original.
export function thumbPath(image) {
  return image ? image.replace(`${PHOTO_BASE}/`, `${PHOTO_BASE}/thumb/`) : null;
}
